import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../components/layout';
import { Calendar as CalendarIcon, Plus, Filter, Lightbulb } from 'lucide-react';
import { CalendarView } from '../components/calendar/CalendarView';
import { WeekView } from '../components/calendar/WeekView';
import { PostPreview } from '../components/calendar/PostPreview';
import { ScheduleModal } from '../components/calendar/ScheduleModal';
import { type PostStatus } from '../components/common/PostStatusDropdown';
import { SocialIcon } from '../components/icons/SocialIcon';
import { useCalendarUrlState } from '../store/calendarStore';
import type { Post } from '../types/calendar';
import type { Brand } from '../types';
import type { SocialPlatform } from '../components/icons/SocialIcon';
import { usePosts } from '../hooks/usePosts';
import { useUpdatePost } from '../hooks/useUpdatePost';
import { CreatePostModal } from '../components/posts/CreatePostModal';
import { IdeaModal } from '../components/posts/IdeaModal';
import { IdeaDetailModal } from '../components/posts/IdeaDetailModal';
import { useCreatePost } from '../hooks/useCreatePost';
import { useDeletePost } from '../hooks/useDeletePost';
import { MediaDisplay } from '../components/posts/previews/MediaDisplay';
import { PdfThumb } from '../components/common/PdfThumb';
import { type MediaFormat } from '../components/common/MediaFormatDropdown';
import { type PostFormat } from '../components/common/PostFormatDropdown';
import { useToast } from '../context/ToastContext';
import { normalizeMedia } from '../utils/mediaUtils';
// import { normalizeBackendMediaType, type TagVariant } from '../utils/tagStyles';
import { MediaStructureDebug } from '../components/debug/MediaStructureDebug';
 
import { GifLoader } from '../components/website/GifLoader';

const Calendar = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);
  const [calendarViewType, setCalendarViewType] = React.useState<'month' | 'week'>('month');
  

  
  // States for filters slider (all filters)
  const [isFiltersOpen, setIsFiltersOpen] = React.useState(false);
  const [selectedStatuses, setSelectedStatuses] = React.useState<PostStatus[]>([]);
  const [selectedBrandIds, setSelectedBrandIds] = React.useState<string[]>([]);
  const [selectedMediaFormats, setSelectedMediaFormats] = React.useState<MediaFormat[]>([]);
  const [selectedPostFormats, setSelectedPostFormats] = React.useState<PostFormat[]>([]);
  
  // States for draft cards hover preview
  const [hoveredDraftPost, setHoveredDraftPost] = React.useState<string | null>(null);
  const [mousePosition, setMousePosition] = React.useState({ x: 0, y: 0 });
  const previewRef = React.useRef<HTMLDivElement | null>(null);
  const [previewSize, setPreviewSize] = React.useState<{ width: number; height: number }>({ width: 0, height: 0 });
  
  // States for drag and drop scheduling
  const [scheduleModalOpen, setScheduleModalOpen] = React.useState(false);
  const [draggedPost, setDraggedPost] = React.useState<Post | null>(null);
  const [dropDate, setDropDate] = React.useState<Date | null>(null);
  const [dragOverDate, setDragOverDate] = React.useState<Date | null>(null);
  
  // State for idea modal
  const [isIdeaModalOpen, setIsIdeaModalOpen] = React.useState(false);
  const [ideaModalInitialDate, setIdeaModalInitialDate] = React.useState<Date | undefined>(undefined);
  const createPostMutation = useCreatePost();
  const deletePostMutation = useDeletePost();

  // State for idea detail modal (when clicking an idea on the calendar)
  const [selectedIdea, setSelectedIdea] = React.useState<Post | null>(null);
  const [isIdeaDetailOpen, setIsIdeaDetailOpen] = React.useState(false);

  // State for media debug mode
  const [debugMode, setDebugMode] = React.useState(false);

  // State for column visibility in table
  const [columnVisibility, setColumnVisibility] = React.useState({
    brand: true,
    created: true,
    updated: true,
    published: true
  });

  const {
    currentDate, 
    setCurrentDate
  } = useCalendarUrlState();

  // Compute month range for the currently displayed date
  const monthStart = React.useMemo(() => new Date(currentDate.getFullYear(), currentDate.getMonth(), 1), [currentDate]);
  const monthEnd = React.useMemo(() => new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0), [currentDate]);
  const { data: postsData, isLoading: isLoadingPosts, isFetching: isFetchingPosts, error: postsError } = usePosts({
    start: monthStart,
    end: monthEnd,
    applanguage: i18n.language,
  });
  const posts = (postsData?.posts || []) as Post[];

  // Linkify hashtags and URLs in draft sidebar snippets
  const renderLinkedText = (text: string, colorHex = '#0B65C2') => {
    const elements: React.ReactNode[] = [];
    const regex = /(https?:\/\/[^\s]+|www\.[^\s]+|#[\p{L}\d_]+)/giu;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      const start = match.index;
      if (start > lastIndex) {
        elements.push(text.slice(lastIndex, start));
      }
      const token = match[0];
      if (token.startsWith('#')) {
        elements.push(
          <span key={`hash-${start}`} className={`text-[${colorHex}]` as any}>{token}</span>
        );
      } else {
        const href = token.startsWith('http') ? token : `http://${token}`;
        elements.push(
          <a key={`link-${start}`} href={href} target="_blank" rel="noreferrer" className={`hover:underline`} style={{ color: colorHex }}>
            {token}
          </a>
        );
      }
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) {
      elements.push(text.slice(lastIndex));
    }
    return elements;
  };
  const { showToast } = useToast();
  const updatePostMutation = useUpdatePost();

  // Initialize multi-select filters when drawer opens (all filters)
  React.useEffect(() => {
    if (!isFiltersOpen) return;
    
    // No initialization needed since we're using only sidebar filters
    // Users will select what they want from the sidebar
  }, [isFiltersOpen]);

  // Removed visibilitychange-based refresh to avoid unnecessary API calls on tab focus

  // Extract brands for filters
  const brands: Brand[] = React.useMemo(() => {
    const ids = Array.from(new Set(posts.map((p: Post) => p.brandId).filter(Boolean))) as string[];
    return [
      { id: 'all', name: t('brandDropdown.allBrands', 'All Brands'), logo: '/logo.svg' },
      ...ids.map((brandId: string) => {
        const pForBrand = posts.find((p: Post) => p.brandId === brandId);
        return {
          id: brandId,
          name: pForBrand?.brandName || 'Unknown Brand',
          logo: pForBrand?.brandLogo || ''
        } as Brand;
      })
    ];
  }, [posts, t]);

  // Status definitions for filters
  const statuses = React.useMemo(() => [
    { id: 'idea', label: t('statusDropdown.idea', 'Idea'), emoji: '💡', description: t('statusDropdown.ideaDesc', 'A post idea saved for later with an optional reminder date.') },
    { id: 'draft', label: t('statusDropdown.draft', 'Draft'), emoji: '📝', description: t('statusDropdown.draftDesc', 'Post is in progress, not yet submitted for review or scheduling.') },
    { id: 'scheduled', label: t('statusDropdown.scheduled', 'Scheduled'), emoji: '📆', description: t('statusDropdown.scheduledDesc', 'Post is approved and set for automatic publishing at a future time.') },
    { id: 'ready_to_publish', label: t('statusDropdown.ready_to_publish', 'Ready to Publish'), emoji: '🚀', description: t('statusDropdown.ready_to_publishDesc', 'Post is complete, approved, and awaiting its scheduled time or manual publishing.') },
    { id: 'waiting_for_approval', label: t('statusDropdown.waiting_for_approval', 'Waiting for approval'), emoji: '⏳', description: t('statusDropdown.waiting_for_approvalDesc', 'Post has been submitted and is awaiting review by an approver.') },
    { id: 'published', label: t('statusDropdown.published', 'Published'), emoji: '✅', description: t('statusDropdown.publishedDesc', 'Post has been successfully shared on the selected platforms.') },
    { id: 'rejected', label: t('statusDropdown.rejected', 'Rejected'), emoji: '❌', description: t('statusDropdown.rejectedDesc', 'Post was reviewed but not approved for publishing by an approver.') },
    { id: 'error', label: t('statusDropdown.error', 'Error'), emoji: '⚠️', description: t('statusDropdown.errorDesc', 'An error occurred while trying to publish or process the post.') },
    { id: 'processing', label: t('statusDropdown.processing', 'Processing'), emoji: '⚙️', description: t('statusDropdown.processingDesc', 'Post is currently being processed by the system (e.g., media upload, API submission).') },
  ], [t]);

  // Media format definitions for filters (based on API postMediaType field)
  const mediaFormats = React.useMemo(() => [
    { id: 'all', label: t('mediaFormat.all', 'All Media'), emoji: '🌐' },
    { id: 'image', label: t('mediaFormat.image', 'Image'), emoji: '📷' },
    { id: 'video', label: t('mediaFormat.video', 'Video'), emoji: '🎥' },
    { id: 'carousel', label: t('mediaFormat.carousel', 'Carousel'), emoji: '🖼️' },
    { id: 'text', label: t('mediaFormat.text', 'Text'), emoji: '📄' },
  ], [t]);

  // Post format definitions for filters
  const postFormats = React.useMemo(() => [
    { id: 'all', label: t('postFormat.all', 'All Formats'), emoji: '🌐' },
    { id: 'regular', label: t('postFormat.regular', 'Regular'), emoji: '📄' },
    { id: 'reel', label: t('postFormat.reel', 'Reel'), emoji: '🎬' },
  ], [t]);

  // Helper function to determine media format of a post (using API postMediaType field)
  const getPostMediaFormat = (post: Post): MediaFormat => {
    const postMediaType = (post as any).postMediaType;
    if (postMediaType) {
      // Map API values to our MediaFormat type
      switch (postMediaType.toLowerCase()) {
        case 'image': return 'image';
        case 'video': return 'video';
        case 'multi-images': return 'carousel';
        case 'document': return 'text';
        default: return 'all';
      }
    }
    
    // Fallback to previous logic if postMediaType is not available
    const normalized = normalizeMedia(post.media);
    if (!normalized) return 'all';
    if (normalized.type === 'image') return 'image';
    if (normalized.type === 'video') return 'video';
    return 'all';
  };

  // Helper function to get post format
  const getPostFormat = (post: Post): PostFormat => {
    return post.format === 'reel' ? 'reel' : 'regular';
  };

  

  // Filter posts for CalendarView (using only sidebar multi-select filters)
  const filteredPosts = posts.filter((post: Post) => {
    // Exclude drafts from calendar - they'll go in the sidebar
    if (post.status === 'draft') return false;
    
    // Brand filter: use multi-select only
    if (selectedBrandIds.length > 0 && !selectedBrandIds.includes('all')) {
      if (!post.brandId || !selectedBrandIds.includes(post.brandId)) return false;
    }
    
    // Status filter: use multi-select only
    if (selectedStatuses.length > 0) {
      if (!selectedStatuses.includes(post.status)) return false;
    }
    
    // Media format filter: use multi-select only
    if (selectedMediaFormats.length > 0 && !selectedMediaFormats.includes('all')) {
      if (!selectedMediaFormats.includes(getPostMediaFormat(post))) return false;
    }
    
    // Post format filter: use multi-select only
    if (selectedPostFormats.length > 0 && !selectedPostFormats.includes('all')) {
      if (!selectedPostFormats.includes(getPostFormat(post))) return false;
    }
    
    return true;
  });

  // Get draft posts for sidebar (apply same filters as calendar except status is always 'draft')
  const draftPosts = posts.filter((post: Post) => {
    if (post.status !== 'draft') return false;
    
    // Brand filter: use multi-select only
    if (selectedBrandIds.length > 0 && !selectedBrandIds.includes('all')) {
      if (!post.brandId || !selectedBrandIds.includes(post.brandId)) return false;
    }
    
    // Media format filter: use multi-select only
    if (selectedMediaFormats.length > 0 && !selectedMediaFormats.includes('all')) {
      if (!selectedMediaFormats.includes(getPostMediaFormat(post))) return false;
    }
    
    // Post format filter: use multi-select only
    if (selectedPostFormats.length > 0 && !selectedPostFormats.includes('all')) {
      if (!selectedPostFormats.includes(getPostFormat(post))) return false;
    }
    
    return true;
  });

  // Calculate counts for filter badges
  const brandCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    posts.forEach((post: Post) => {
      const brandId = post.brandId || 'unknown';
      counts[brandId] = (counts[brandId] || 0) + 1;
    });
    return counts;
  }, [posts]);

  const statusCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    posts.forEach((post: Post) => {
      const status = post.status || 'unknown';
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [posts]);

  const mediaFormatCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    posts.forEach((post: Post) => {
      const format = getPostMediaFormat(post);
      counts[format] = (counts[format] || 0) + 1;
    });
    return counts;
  }, [posts]);

  const postFormatCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    posts.forEach((post: Post) => {
      const format = getPostFormat(post);
      counts[format] = (counts[format] || 0) + 1;
    });
    return counts;
  }, [posts]);

  const handlePostClick = (post: Post) => {
    // Ideas open a detail modal instead of the edit page
    if (post.status === 'idea') {
      setSelectedIdea(post);
      setIsIdeaDetailOpen(true);
      return;
    }
    // Scroll to top of page for better mobile experience
    window.scrollTo(0, 0);
    // Reuse already-fetched post data to avoid an extra API call
    navigate(`/posts/${post.id}/edit`, { state: { post } });
  };

  // Reserved for future helpers

  // Handlers for the CreatePostModal
  const handleCreateWithAI = () => {
    setIsCreateModalOpen(false);
    navigate('/content-creation');
  };

  const handleCreateManually = () => {
    setIsCreateModalOpen(false);
    navigate('/posts/new');
  };

  const handleCreateIdea = () => {
    setIsCreateModalOpen(false);
    setIdeaModalInitialDate(undefined);
    setIsIdeaModalOpen(true);
  };

  const handleDragStart = (e: React.DragEvent, post: Post) => {
    setDraggedPost(post);
    
    // Create a custom drag image that shows the full card
    const dragElement = e.currentTarget as HTMLElement;
    const rect = dragElement.getBoundingClientRect();
    
    // Clone the element to create a clean drag image
    const clone = dragElement.cloneNode(true) as HTMLElement;
    clone.style.width = rect.width + 'px';
    clone.style.height = rect.height + 'px';
    clone.style.position = 'absolute';
    clone.style.top = '-1000px';
    clone.style.left = '-1000px';
    clone.style.pointerEvents = 'none';
    clone.style.transform = 'none';
    clone.style.opacity = '0.9';
    clone.style.backgroundColor = 'white';
    clone.style.borderRadius = '8px';
    clone.style.border = '1px solid black';
    clone.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
    
    // Add to body temporarily
    document.body.appendChild(clone);
    
    // Set as drag image
    e.dataTransfer.setDragImage(clone, rect.width / 2, rect.height / 2);
    
    // Remove after a short delay
    setTimeout(() => {
      if (document.body.contains(clone)) {
        document.body.removeChild(clone);
      }
    }, 0);
  };

  const handleDrop = (date: Date) => {
    if (draggedPost) {
      console.log('📅 handleDrop - Dropped on date:', date, 'ISO:', date.toISOString());
      setDropDate(date);
      setScheduleModalOpen(true);
    }
  };

  const handleScheduleConfirm = async (scheduledAtUtc: string | null, timezone?: string) => {
    if (draggedPost && scheduledAtUtc) {
      try {
        console.log('📅 handleScheduleConfirm - Received scheduledAtUtc:', scheduledAtUtc, 'timezone:', timezone);
        console.log('📅 handleScheduleConfirm - Original dropDate was:', dropDate, 'ISO:', dropDate?.toISOString());
        
        // Update the post with the new schedule
        const updatePayload = {
          ...draggedPost,
          status: 'scheduled',
          scheduledAt: scheduledAtUtc
        };
        
        await updatePostMutation.mutateAsync({
          postId: draggedPost.id,
          postData: updatePayload,
          isFormData: false
        });
        
        // Show success message
        showToast(
          t('calendar.scheduleSuccess', 'Post scheduled successfully!'),
          'success'
        );
        
        // Reset states
        setDraggedPost(null);
        setDropDate(null);
        setScheduleModalOpen(false);
      } catch (error) {
        console.error('Error scheduling post:', error);
        showToast(
          t('calendar.scheduleError', 'Failed to schedule post. Please try again.'),
          'error'
        );
      }
    }
  };

  const handleScheduleCancel = () => {
    setDraggedPost(null);
    setDropDate(null);
    setScheduleModalOpen(false);
  };

  // Measure preview size whenever it changes or when toggled
  React.useLayoutEffect(() => {
    if (!hoveredDraftPost) return;
    const measure = () => {
      if (previewRef.current) {
        const rect = previewRef.current.getBoundingClientRect();
        // Only update if we have a sensible size
        if (rect.width > 0 && rect.height > 0) {
          setPreviewSize({ width: rect.width, height: rect.height });
        }
      }
    };
    // Measure on mount and on next frame to capture layout
    measure();
    const rAF = requestAnimationFrame(measure);
    // Observe dynamic size changes (media load, text wrapping, etc.)
    let observer: ResizeObserver | null = null;
    if (previewRef.current && 'ResizeObserver' in window) {
      observer = new ResizeObserver(() => {
        // Use rAF to avoid layout thrash during rapid size changes
        requestAnimationFrame(() => {
          if (previewRef.current) {
            const rect = previewRef.current.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
              setPreviewSize({ width: Math.ceil(rect.width), height: Math.ceil(rect.height) });
            }
          }
        });
      });
      observer.observe(previewRef.current);
    }
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(rAF);
      window.removeEventListener('resize', measure);
      if (observer) observer.disconnect();
    };
  }, [hoveredDraftPost]);

  const getOptimalPosition = () => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Fallback dimensions if not measured yet
    const fallbackWidth = Math.min(800, viewportWidth * 0.9);
    const fallbackHeight = Math.min(500, viewportHeight * 0.8);
    const rawWidth = previewSize.width || fallbackWidth;
    const rawHeight = previewSize.height || fallbackHeight;
    const margin = 20;

    // Clamp dimensions to viewport
    const cardWidth = Math.min(rawWidth, viewportWidth - margin * 2);
    const cardHeight = Math.min(rawHeight, viewportHeight - margin * 2);

    let x = mousePosition.x + 10; // Prefer right of cursor
    let y = mousePosition.y - 100; // Prefer above cursor

    // Clamp horizontally
    if (x + cardWidth + margin > viewportWidth) {
      x = mousePosition.x - cardWidth - 10;
    }
    if (x < margin) x = margin;
    // If still overflowing (e.g., card wider than viewport), pin to margins
    if (cardWidth + margin * 2 > viewportWidth) {
      x = margin;
    }

    // Clamp vertically
    if (y + cardHeight + margin > viewportHeight) {
      y = viewportHeight - cardHeight - margin;
    }
    if (y < margin) y = margin;
    // If still overflowing vertically, pin to margins
    if (cardHeight + margin * 2 > viewportHeight) {
      y = margin;
    }

    return { x, y, width: cardWidth, height: cardHeight };
  };

  // Loading state
  if (isLoadingPosts) {
    // Skeleton only when not actively calling API (or initial layout before data)
    return (
      <div className="min-h-screen bg-white lg:ml-16 pt-4 lg:pt-0 animate-pulse overflow-x-hidden">
        <div className="w-full max-w-[95%] mx-auto px-2 sm:px-4 py-8">
          {/* Header Skeleton */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
            <div className="flex-1 min-w-0">
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
            <div className="h-10 bg-gray-200 rounded-lg w-32"></div>
          </div>
          {/* Filters Skeleton */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
            <div className="flex-1"></div> {/* Spacer */} 
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <div className="h-8 sm:h-10 bg-gray-200 rounded-lg w-full sm:w-32 lg:w-40"></div>
              <div className="h-8 sm:h-10 bg-gray-200 rounded-lg w-full sm:w-32 lg:w-40"></div>
            </div>
          </div>
          {/* CalendarView Skeleton */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-2 sm:p-4 lg:p-6 max-w-full overflow-hidden">
            {/* Calendar Header Skeleton */}
            <div className="flex justify-between items-center mb-4">
              <div className="h-6 w-6 sm:h-8 sm:w-8 bg-gray-200 rounded-full"></div>
              <div className="h-5 sm:h-6 bg-gray-200 rounded w-1/3"></div>
              <div className="h-6 w-6 sm:h-8 sm:w-8 bg-gray-200 rounded-full"></div>
            </div>
            {/* Calendar Grid Skeleton */}
            <div className="grid grid-cols-7 gap-0.5 sm:gap-1 text-center text-xs font-semibold text-gray-500 mb-2">
              {[...Array(7)].map((_, i) => (
                <div key={i} className="h-3 sm:h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-px border-t border-l overflow-hidden">
              {[...Array(35)].map((_, i) => (
                <div key={i} className="relative bg-gray-50 border-r border-b h-16 sm:h-24 lg:h-32">
                  <div className="absolute top-0.5 left-0.5 sm:top-1 sm:left-1 h-3 w-3 sm:h-4 sm:w-4 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Note: We removed the error state blocking return to always show the calendar
  // Even if posts fail to load, users can still view and use the calendar interface

  return (
    <>
      {isFetchingPosts && (
        <GifLoader
          title={t('calendar.loading.title', 'Loading your calendar...')}
          currentStep={t('calendar.loading.refreshing', 'Refreshing posts...')}
          videoSrc="/Panda - Gold Digger.mov"
        />
      )}
      <PageLayout
        title={t('calendar.title')}
        subtitle={
          <div>
            {t('calendar.subtitle')}
            {postsError && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg text-sm">
                <p className="text-sm text-red-600">
                  ⚠️ {t('calendar.errorLoadingPosts', 'Unable to load some posts. The calendar may not show all scheduled content.')}
                </p>
              </div>
            )}
          </div>
        }
        actions={
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setIdeaModalInitialDate(undefined); setIsIdeaModalOpen(true); }}
              className="inline-flex items-center px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
            >
              <Lightbulb className="w-5 h-5 mr-2" />
              {t('calendar.addIdea', 'Add Idea')}
            </button>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('sidebar.createContent')}
            </button>
          </div>
        }
        className="overflow-x-hidden"
        headerClassName="w-full max-w-[100vw] mx-auto px-2 sm:px-4 overflow-x-hidden"
      >

          {/* Add Filters Section Back */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8 overflow-x-auto">
             <div className="flex items-center gap-2">
               {/* Calendar View Type Toggles */}
               <button
                 onClick={() => setCalendarViewType('month')}
                 className={`inline-flex items-center px-2 sm:px-3 py-1.5 rounded-lg text-sm ${calendarViewType === 'month' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
               >
                 <CalendarIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                 Mois
               </button>
               <button
                 onClick={() => setCalendarViewType('week')}
                 className={`inline-flex items-center px-2 sm:px-3 py-1.5 rounded-lg text-sm ${calendarViewType === 'week' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
               >
                 <CalendarIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                 Semaine
               </button>
             </div>
             <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
               {/* Filters button */}
               {(() => {
                 const activeFiltersCount = (selectedStatuses.length > 0 ? 1 : 0) + 
                   (selectedBrandIds.length > 0 ? 1 : 0) + 
                   (selectedMediaFormats.length > 0 ? 1 : 0) + 
                   (selectedPostFormats.length > 0 ? 1 : 0);
                 return (
                   <div className="relative">
                     <button
                       onClick={() => setIsFiltersOpen((v) => !v)}
                       className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:text-primary-600"
                       aria-expanded={isFiltersOpen}
                     >
                       <Filter className="w-4 h-4 mr-2 text-primary-500" />
                       {t('calendar.filters', 'Filtres')}
                       {activeFiltersCount > 0 && (
                         <span className="ml-2 inline-flex items-center justify-center rounded-full bg-primary-600 text-white text-xs w-5 h-5">
                           {activeFiltersCount}
                         </span>
                       )}
                     </button>
                   </div>
                 );
               })()}
             </div>
          </div>

          {/* CalendarView */}
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
            {/* Main Calendar */}
            <div className="flex-1 min-w-0">
              {calendarViewType === 'month' ? (
                <CalendarView
                  currentDate={currentDate}
                  onDateChange={setCurrentDate}
                  posts={filteredPosts}
                  onPostClick={handlePostClick}
                  onDrop={handleDrop}
                  dragOverDate={dragOverDate}
                  onDragOver={setDragOverDate}
                  onPostDragStart={handleDragStart}
                />
              ) : (
                <WeekView
                  currentDate={currentDate}
                  onDateChange={setCurrentDate}
                  posts={filteredPosts}
                  onPostClick={handlePostClick}
                  onDrop={handleDrop}
                  dragOverDate={dragOverDate}
                  onDragOver={setDragOverDate}
                  onPostDragStart={handleDragStart}
                />
              )}
            </div>

            {/* Drafts Sidebar */}
            <div className="w-full lg:w-80 bg-white rounded-xl border border-black shadow-lg p-3 sm:p-4 mt-4 lg:mt-0">
              <div className="flex items-center gap-2 mb-4">
                <span className="bg-orange-100 text-orange-700 text-xs font-medium px-2.5 py-0.5 rounded-full">
                  {draftPosts.length}
                </span>
                <h3 className="font-semibold text-gray-900">Non planifiés</h3>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Glissez et déposez ces brouillons sur le calendrier pour les programmer.
              </p>
              
              {draftPosts.length > 0 ? (
                <div className="space-y-3">
                  {draftPosts.map((post) => (
                    <div
                      key={post.id}
                      draggable
                      onClick={() => handlePostClick(post)}
                      className="cursor-move p-3 border border-black rounded-lg hover:bg-gray-50 transition-colors"
                      onDragStart={(e) => handleDragStart(e, post)}
                      onMouseEnter={(e) => {
                        setHoveredDraftPost(post.id);
                        setMousePosition({ x: e.clientX, y: e.clientY });
                      }}
                      onMouseMove={(e) => {
                        if (hoveredDraftPost === post.id) {
                          setMousePosition({ x: e.clientX, y: e.clientY });
                        }
                      }}
                      onMouseLeave={() => setHoveredDraftPost(null)}
                    >
                      <div className="flex items-start gap-3">
                        {(() => {
                          const normalizedMedia = normalizeMedia(post.media);
                          if (!normalizedMedia) return null;
                          const isPdf = /\.pdf(\?|#|$)/i.test(normalizedMedia.url);
                          return (
                            <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded overflow-visible relative">
                              {isPdf ? (
                                <PdfThumb src={normalizedMedia.url} className="w-full h-full" frame="square" />
                              ) : (
                                <MediaDisplay
                                  mediaUrl={normalizedMedia.url}
                                  altText="Post media"
                                  type={normalizedMedia.type}
                                  className="w-full h-full"
                                  objectFit="cover"
                                  controls={false}
                                  autoPlayOnMount={normalizedMedia.type === 'video'}
                                  muted
                                />
                              )}
                            </div>
                          );
                        })()}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-2 mb-2">
                            <p className="text-sm text-gray-900 line-clamp-2 flex-1">
                              {renderLinkedText(post.content)}
                            </p>
                            {!post.media && (
                              <span className="text-[10px] bg-gray-100 px-1 py-0.5 rounded flex-shrink-0">📝</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {post.brandLogo && (
                              <img 
                                src={post.brandLogo} 
                                alt={post.brandName || 'Brand logo'}
                                className="w-4 h-4 rounded object-cover"
                              />
                            )}
                            <div className="flex gap-1">
                              {post.platforms.map((platform: string) => (
                                <SocialIcon 
                                  key={platform}
                                  platform={platform as SocialPlatform}
                                  size={14}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Debug Mode - Show Media Structure */}
                      {debugMode && (
                        <div className="mt-2">
                          <MediaStructureDebug 
                            media={post.media} 
                            postId={post.id}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-2">📝</div>
                  <p className="text-sm text-gray-500">Aucun brouillon en attente</p>
                </div>
              )}
            </div>
          </div>
      </PageLayout>

      {/* PostPreview for draft hover */}
      {hoveredDraftPost && (() => {
        const pos = getOptimalPosition();
        return (
          <div
            ref={previewRef}
            className="fixed pointer-events-none z-50 overflow-auto"
            style={{
              left: pos.x,
              top: pos.y,
              width: pos.width,
              height: pos.height,
              maxWidth: '95vw',
              maxHeight: '90vh'
            }}
          >
            <PostPreview
              post={draftPosts.find((p) => p.id === hoveredDraftPost)!}
            />
          </div>
        );
      })()}

      {/* Right-side sliding Filters drawer */}
      {isFiltersOpen && (
        <div className="fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setIsFiltersOpen(false)}
          />
          {/* Panel */}
          <div className="absolute right-0 top-0 h-full w-full sm:w-[380px] bg-white shadow-2xl border-l border-gray-200 transform transition-transform duration-300 ease-out">
            <div className="h-full flex flex-col">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">{t('calendar.filters', 'Filtres')}</h3>
                <button
                  onClick={() => setIsFiltersOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
                {/* Status group */}
                <div>
                  <div className="text-sm font-semibold text-gray-900 mb-3">{t('calendar.filter.status', 'Statut')}</div>
                  <div className="space-y-3">
                    {statuses.map(s => (
                      <div key={s.id} className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          className="mt-1 rounded border-gray-300 text-primary-600"
                          checked={selectedStatuses.includes(s.id as PostStatus)}
                          onChange={(e) => {
                            setSelectedStatuses(prev => e.target.checked ? [...prev, s.id as PostStatus] : prev.filter(v => v !== (s.id as PostStatus)));
                          }}
                        />
                        <div className="min-w-0">
                          <div className="text-sm text-gray-900 flex items-center gap-2">
                            <span className="shrink-0">{s.emoji}</span>
                            <span className="truncate font-semibold">{s.label} ({statusCounts[s.id] ?? 0})</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5 leading-snug">
                            {s.description}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Media Format group */}
                <div>
                  <div className="text-sm font-semibold text-gray-900 mb-3">{t('calendar.filter.mediaFormat', 'Type de média')}</div>
                  <div className="space-y-3">
                    {mediaFormats.filter(f => f.id !== 'all').map(f => (
                      <label key={f.id} className="flex items-center gap-3 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-primary-600"
                          checked={selectedMediaFormats.includes(f.id as MediaFormat)}
                          onChange={(e) => {
                            setSelectedMediaFormats(prev => e.target.checked ? [...prev, f.id as MediaFormat] : prev.filter(v => v !== (f.id as MediaFormat)));
                          }}
                        />
                        <span className="inline-flex items-center gap-2">
                          <span>{f.emoji}</span>
                          <span className="truncate max-w-[220px]">
                            {f.label} ({mediaFormatCounts[f.id] ?? 0})
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Post Format group */}
                <div>
                  <div className="text-sm font-semibold text-gray-900 mb-3">{t('calendar.filter.postFormat', 'Format du post')}</div>
                  <div className="space-y-3">
                    {postFormats.filter(f => f.id !== 'all').map(f => (
                      <label key={f.id} className="flex items-center gap-3 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-primary-600"
                          checked={selectedPostFormats.includes(f.id as PostFormat)}
                          onChange={(e) => {
                            setSelectedPostFormats(prev => e.target.checked ? [...prev, f.id as PostFormat] : prev.filter(v => v !== (f.id as PostFormat)));
                          }}
                        />
                        <span className="inline-flex items-center gap-2">
                          <span>{f.emoji}</span>
                          <span className="truncate max-w-[220px]">
                            {f.label} ({postFormatCounts[f.id] ?? 0})
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Brand group */}
                <div>
                  <div className="text-sm font-semibold text-gray-900 mb-3">{t('calendar.filter.brand', 'Marque')}</div>
                  <div className="space-y-3">
                    {brands.map(b => (
                      <label key={b.id} className="flex items-center gap-3 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-primary-600"
                          checked={selectedBrandIds.includes(b.id)}
                          onChange={(e) => {
                            if (b.id === 'all') {
                              setSelectedBrandIds(e.target.checked ? ['all'] : []);
                              return;
                            }
                            setSelectedBrandIds(prev => {
                              const withoutAll = prev.filter(id => id !== 'all');
                              if (e.target.checked) {
                                if (withoutAll.includes(b.id)) return withoutAll;
                                return [...withoutAll, b.id];
                              }
                              return withoutAll.filter(v => v !== b.id);
                            });
                          }}
                        />
                        <span className="inline-flex items-center gap-2">
                          {b.id !== 'all' && b.logo ? (
                            <img src={b.logo} alt={b.name} className="w-5 h-5 rounded object-contain" />
                          ) : null}
                          <span className="truncate max-w-[220px]">
                            {b.name}{b.id !== 'all' ? ` (${brandCounts[b.id] ?? 0})` : ''}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Columns group */}
                <div>
                  <div className="text-sm font-semibold text-gray-900 mb-3">{t('calendar.filter.columns', 'Colonnes')}</div>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-primary-600"
                        checked={columnVisibility.brand}
                        onChange={(e) => setColumnVisibility(prev => ({ ...prev, brand: e.target.checked }))}
                      />
                      <span className="truncate max-w-[220px]">
                        {t('calendar.columns.brand', 'Marque')}
                      </span>
                    </label>
                    <label className="flex items-center gap-3 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-primary-600"
                        checked={columnVisibility.created}
                        onChange={(e) => setColumnVisibility(prev => ({ ...prev, created: e.target.checked }))}
                      />
                      <span className="truncate max-w-[220px]">
                        {t('calendar.columns.created', 'Créé')}
                      </span>
                    </label>
                    <label className="flex items-center gap-3 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-primary-600"
                        checked={columnVisibility.updated}
                        onChange={(e) => setColumnVisibility(prev => ({ ...prev, updated: e.target.checked }))}
                      />
                      <span className="truncate max-w-[220px]">
                        {t('calendar.columns.updated', 'Modifié')}
                      </span>
                    </label>
                    <label className="flex items-center gap-3 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-primary-600"
                        checked={columnVisibility.published}
                        onChange={(e) => setColumnVisibility(prev => ({ ...prev, published: e.target.checked }))}
                      />
                      <span className="truncate max-w-[220px]">
                        {t('calendar.columns.published', 'Publié')}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedStatuses([]);
                    setSelectedBrandIds([]);
                    setSelectedMediaFormats([]);
                    setSelectedPostFormats([]);
                    setColumnVisibility({
                      brand: true,
                      created: true,
                      updated: true,
                      published: true
                    });
                  }}
                  className="px-4 py-2 text-sm rounded-lg text-gray-700 hover:text-primary-600"
                >
                  {t('common.reset', 'Réinitialiser')}
                </button>
                <div />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Render the modals */}
      <CreatePostModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateWithAI={handleCreateWithAI}
        onCreateManually={handleCreateManually}
        onCreateIdea={handleCreateIdea}
      />
      
      <ScheduleModal
        isOpen={scheduleModalOpen}
        onClose={handleScheduleCancel}
        onConfirm={handleScheduleConfirm}
        post={draggedPost}
        initialDate={dropDate || undefined}
      />

      <IdeaModal
        isOpen={isIdeaModalOpen}
        onClose={() => setIsIdeaModalOpen(false)}
        initialDate={ideaModalInitialDate}
        isLoading={createPostMutation.isPending}
        onSave={async (idea) => {
          try {
            await createPostMutation.mutateAsync({
              postData: {
                title: idea.title,
                description: idea.description,
                brandId: idea.brandId,
                status: 'idea',
                reminderAt: idea.reminderAt,
                platforms: {},
                content: '',
              }
            });
            showToast(t('calendar.ideaSaved', 'Idea saved!'), 'success');
            setIsIdeaModalOpen(false);
          } catch (error) {
            console.error('Error saving idea:', error);
            showToast(t('calendar.ideaError', 'Failed to save idea.'), 'error');
          }
        }}
      />

      <IdeaDetailModal
        isOpen={isIdeaDetailOpen}
        onClose={() => { setIsIdeaDetailOpen(false); setSelectedIdea(null); }}
        idea={selectedIdea}
        isPromoting={updatePostMutation.isPending}
        isDeleting={deletePostMutation.isPending}
        onUpdate={async (ideaId, data) => {
          try {
            await updatePostMutation.mutateAsync({
              postId: ideaId,
              postData: { title: data.title, description: data.description, reminderAt: data.reminderAt },
              isFormData: false
            });
            showToast(t('ideaDetail.ideaUpdated', 'Idea updated!'), 'success');
            setIsIdeaDetailOpen(false);
            setSelectedIdea(null);
          } catch (error) {
            console.error('Error updating idea:', error);
            showToast(t('calendar.ideaError', 'Failed to save idea.'), 'error');
          }
        }}
        onPromoteToDraft={async (idea) => {
          try {
            await updatePostMutation.mutateAsync({
              postId: idea.id,
              postData: { status: 'draft' },
              isFormData: false
            });
            showToast(t('calendar.promotedToDraft', 'Idea promoted to draft!'), 'success');
            setIsIdeaDetailOpen(false);
            setSelectedIdea(null);
            navigate(`/posts/${idea.id}/edit`);
          } catch (error) {
            console.error('Error promoting idea:', error);
            showToast(t('calendar.promoteError', 'Failed to promote idea.'), 'error');
          }
        }}
        onDelete={async (ideaId) => {
          try {
            await deletePostMutation.mutateAsync(ideaId);
            showToast(t('calendar.ideaDeleted', 'Idea deleted.'), 'success');
            setIsIdeaDetailOpen(false);
            setSelectedIdea(null);
          } catch (error) {
            console.error('Error deleting idea:', error);
            showToast(t('calendar.ideaDeleteError', 'Failed to delete idea.'), 'error');
          }
        }}
      />
    </>
  );
};

export default Calendar;